import React, { useState } from 'react';
import styled from '@emotion/styled';

import { Icon } from '../lumen';
import { Dropdown } from './Dropdown';
import { WorkspacePicker } from './WorkspacePicker';
import { usePersistedState } from './usePersistedState';

export const SideNav = ({
  activeTab = 'projects',
  workspaceName = 'Workspace',
  workspaces = [],
  onWorkspaceChange,
  onSelectProjects,
  onSelectSettings,
}) => {
  const [collapsed, setCollapsed] = usePersistedState('sideNavCollapsed', false);
  const [pinned, setPinned] = usePersistedState('sideNavPinned', true);
  const [hovering, setHovering] = useState(false);

  const effectivelyCollapsed = collapsed && (pinned || !hovering);

  const workspaceInitial = (workspaceName || 'W').charAt(0).toUpperCase();
  const hasSwitcher = workspaces.length > 1;

  const workspaceTab = (
    <WorkspaceTab
      type="button"
      $interactive={hasSwitcher}
      $collapsed={effectivelyCollapsed}
      title={effectivelyCollapsed ? workspaceName : undefined}
      aria-label={hasSwitcher ? 'Switch workspace' : undefined}
    >
      <WorkspaceAvatar>{workspaceInitial}</WorkspaceAvatar>
      {!effectivelyCollapsed && <WorkspaceName>{workspaceName}</WorkspaceName>}
    </WorkspaceTab>
  );

  return (
    <Nav
      $collapsed={effectivelyCollapsed}
      onMouseEnter={() => !pinned && collapsed && setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <Tabs>
        {hasSwitcher ? (
          <Dropdown block={!effectivelyCollapsed} trigger={workspaceTab}>
            {({ close }) => (
              <WorkspacePicker
                workspaces={workspaces}
                onSelect={onWorkspaceChange}
                onClose={close}
              />
            )}
          </Dropdown>
        ) : (
          workspaceTab
        )}
        <TabItem
          type="button"
          $active={activeTab === 'projects'}
          $collapsed={effectivelyCollapsed}
          onClick={onSelectProjects}
          aria-label={effectivelyCollapsed ? 'Projects' : undefined}
          title={effectivelyCollapsed ? 'Projects' : undefined}
        >
          <Icon name="file-chart-column" size={20} />
          {!effectivelyCollapsed && <span>Projects</span>}
        </TabItem>
        <TabItem
          type="button"
          $active={activeTab === 'settings'}
          $collapsed={effectivelyCollapsed}
          onClick={onSelectSettings}
          aria-label={effectivelyCollapsed ? 'Settings' : undefined}
          title={effectivelyCollapsed ? 'Settings' : undefined}
        >
          <Icon name="gear" size={20} />
          {!effectivelyCollapsed && <span>Settings</span>}
        </TabItem>
      </Tabs>
      <BottomBar $collapsed={effectivelyCollapsed}>
        <PinButton
          type="button"
          $pinned={pinned}
          $collapsed={effectivelyCollapsed}
          onClick={() => setPinned(p => !p)}
          aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
          aria-pressed={pinned}
          title={
            pinned
              ? 'Unpin — sidebar will auto-collapse when unpinned + collapsed'
              : 'Pin — keep sidebar in its current state'
          }
        >
          <Icon name="thumbtack-angle" size={16} />
          {!effectivelyCollapsed && <span>Pin</span>}
        </PinButton>
        <CollapseButton
          type="button"
          onClick={() => setCollapsed(c => !c)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            name={collapsed ? 'circle-chevron-right' : 'circle-chevron-left'}
            size={16}
          />
        </CollapseButton>
      </BottomBar>
    </Nav>
  );
};

const Nav = styled.nav`
  position: relative;
  width: ${({ $collapsed }) => ($collapsed ? '52px' : '200px')};
  flex-shrink: 0;
  background: ${({ theme }) => theme.color.bg.default};
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  padding: 16px 8px 56px;
  transition: width 150ms ease;
`;

const Tabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WorkspaceTab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  width: 100%;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  text-align: left;

  &:hover {
    background: ${({ $interactive, theme }) =>
      $interactive ? theme.color.bg.subtle : 'transparent'};
  }
`;

const WorkspaceAvatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #10b981;
  color: ${({ theme }) => theme.color.bg.default};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
`;

const WorkspaceName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 16px;
  color: ${({ theme }) => theme.color.fg.default};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TabItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 4px 8px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  background: ${({ $active, theme }) =>
    $active ? theme.color.blue[50] : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.color.blue[800] : theme.color.slate[800]};

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.color.blue[50] : theme.color.bg.subtle};
  }
`;

const BottomBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) =>
    $collapsed ? 'center' : 'space-between'};
  padding: 8px 16px;
  border-top: 1px solid #f0f0f0;
`;

const PinButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-size: 12px;
  padding: 0;

  /* Pinned = active/pressed accent color. Unpinned = muted so it reads as
     the "off" state. */
  color: ${({ theme, $pinned }) =>
    $pinned ? theme.color.primary.base : theme.color.fg.subtle};

  &:hover {
    color: ${({ theme, $pinned }) =>
      $pinned ? theme.color.primary.hover : theme.color.fg.default};
  }
`;

const CollapseButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.color.fg.subtle};
  padding: 0;
  display: inline-flex;

  &:hover {
    color: ${({ theme }) => theme.color.fg.default};
  }
`;
